import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, Award, Heart } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [cedula, setCedula] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Authenticate with Strapi via proxy backend
      const response = await api.post(`/auth/strapi-login`, {
        identifier: username,
        password: password
      });

      if (response.data.success && response.data.jwt) {
        // Store user data
        const userData = response.data.user;
        setCedula(userData.ci || '');
        setEmail(userData.email || '');
        setIsAuthenticated(true);
        toast.success('¡Bienvenido!');
        
        // Auto-submit validation
        setTimeout(() => {
          validateBeneficiary(userData.ci, userData.email);
        }, 500);
      } else {
        toast.error(response.data.error || 'Usuario o contraseña incorrectos');
      }
    } catch (error) {
      console.error('Error de login:', error.response?.data || error.message);
      
      if (error.response?.status === 504) {
        toast.error('No se puede conectar al servidor. Por favor, intenta nuevamente.');
      } else if (error.response?.status === 500) {
        toast.error('Error en el servidor. Por favor, contacta al administrador.');
      } else {
        toast.error('Error al iniciar sesión. Por favor, verifica tus credenciales.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateBeneficiary = async (userCi, userEmail) => {
    try {
      const response = await api.post(`/validate-beneficiary`, {
        cedula: userCi,
        email: userEmail
      });

      if (response.data.found) {
        navigate(`/cliente/${userCi}`);
      } else {
        setModalMessage(response.data.message);
        setShowModal(true);
        setTimeout(() => {
          setShowModal(false);
        }, 5000);
      }
    } catch (error) {
      toast.error('Error al validar beneficiario');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`/validate-beneficiary`, {
        cedula,
        email
      });

      if (response.data.found) {
        navigate(`/cliente/${cedula}`);
      } else {
        setModalMessage(response.data.message);
        setShowModal(true);
        setTimeout(() => {
          setShowModal(false);
          setCedula('');
          setEmail('');
        }, 5000);
      }
    } catch (error) {
      toast.error('Error al validar beneficiario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] relative overflow-hidden">
      {/* Fondo Bandera Ondeando Sutil */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 flex justify-center items-center opacity-5">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="currentColor" className="w-[150%] h-auto text-white opacity-20 transform -rotate-12">
          <defs>
            <pattern id="checkers" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
              <rect fill="currentColor" x="0" y="0" width="5" height="5" opacity="0.5"/>
              <rect fill="currentColor" x="5" y="5" width="5" height="5" opacity="0.5"/>
            </pattern>
          </defs>
          <path d="M2 10c10 5 20-5 30 0s20 5 30 0 20-5 30 0v60c-10-5-20 5-30 0s-20-5-30 0-20 5-30 0z"/>
          <path fill="url(#checkers)" d="M2 10c10 5 20-5 30 0s20 5 30 0 20-5 30 0v60c-10-5-20 5-30 0s-20-5-30 0-20 5-30 0z"/>
        </svg>
      </div>
      
      {/* Glowing Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#fbb040] rounded-full blur-[150px] opacity-20"
        />
        
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#1f2d5a] rounded-full blur-[120px] opacity-30"
        />
      </div>
      
      <button
        onClick={() => navigate('/admin/login')}
        className="absolute top-6 right-6 px-6 py-2 text-white bg-[#2A2A2A] border-2 border-[#666666] rounded-lg text-sm transition-all duration-300 hover:border-[#F5A623] hover:bg-[#F5A623] hover:text-black hover:shadow-lg hover:shadow-[#F5A623]/50 hover:-translate-y-1 z-[9999] font-semibold cursor-pointer"
        data-testid="admin-login-link"
      >
        Login Admin
      </button>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          {/* Logo Premium con múltiples efectos */}
          <motion.div className="relative mb-12">
            {/* Backdrop Gradient Animado */}
            <motion.div
              animate={{ 
                background: [
                  'radial-gradient(circle, rgba(245,166,35,0.3) 0%, rgba(255,149,0,0.2) 50%, transparent 100%)',
                  'radial-gradient(circle, rgba(255,149,0,0.3) 0%, rgba(245,166,35,0.2) 50%, transparent 100%)',
                  'radial-gradient(circle, rgba(245,166,35,0.3) 0%, rgba(255,149,0,0.2) 50%, transparent 100%)'
                ]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 blur-3xl"
              style={{ width: '120%', height: '120%', left: '-10%', top: '-10%' }}
            />
            
            {/* Glow Pulsante */}
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 40px 20px rgba(245,166,35,0.4)',
                  '0 0 80px 40px rgba(255,149,0,0.6)',
                  '0 0 40px 20px rgba(245,166,35,0.4)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full"
              style={{ filter: 'blur(20px)' }}
            />
            
            {/* Borde Brillante Rotando */}
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
              style={{ 
                background: 'conic-gradient(from 0deg, transparent 0%, rgba(245,166,35,0.8) 50%, transparent 100%)',
                borderRadius: '50%',
                filter: 'blur(8px)',
                width: '110%',
                height: '110%',
                left: '-5%',
                top: '-5%'
              }}
            />
            
            {/* Logo con Float */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_seo-enhanced-kompa/artifacts/dmk050na_KOMPA%204.png"
              alt="KOMPA Logo"
              className="h-32 mx-auto relative z-10"
              style={{ 
                filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5)) drop-shadow(0 10px 30px rgba(245, 166, 35, 0.3))'
              }}
              animate={{ 
                y: [0, -15, 0],
                scale: [1, 1.02, 1]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
          </motion.div>
          
          {/* Texto Animado con Gradiente */}
          <motion.h1 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mb-6"
          >
            <div className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight">
              <motion.span
                animate={{ 
                  color: ['#ffffff', '#fbb040', '#ffffff'],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="block mb-2"
              >
                BIENVENIDOS A
              </motion.span>
              <span className="inline-flex items-center gap-3">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] animate-gradient">
                  LA META
                </span>
                <span className="text-5xl md:text-6xl lg:text-7xl">🏁</span>
              </span>
            </div>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-2xl md:text-3xl text-white mb-8 font-semibold"
          >
            COMPRUEBA SI ERES BENEFICIADO
          </motion.p>

          {/* Feature Pills Animados */}
          <motion.div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { icon: CheckCircle, text: 'Validación Rápida', gradient: 'from-green-400 to-emerald-500' },
              { icon: Award, text: 'Logra tu Meta', gradient: 'from-[#fbb040] to-yellow-500' },
              { icon: Heart, text: 'Apoyo 24/7', gradient: 'from-red-400 to-pink-500' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1, type: "spring" }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                className={`flex items-center gap-2 bg-gradient-to-r ${item.gradient} text-white px-5 py-3 rounded-full shadow-xl font-bold border-2 border-white/30`}
              >
                <item.icon size={22} />
                <span>{item.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Grid: Formulario y Kompaisito */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            {/* Formulario de Login o Validación */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="glass-card rounded-2xl p-8"
              data-testid="validation-form"
            >
              {!isAuthenticated ? (
                <form onSubmit={handleLogin} className="space-y-6">
                  <h3 className="text-2xl font-bold text-[#F5A623] mb-4 text-center">
                    Inicia Sesión
                  </h3>
                  <div>
                    <Label htmlFor="username" className="text-white text-sm uppercase tracking-wider mb-2 block">
                      Usuario o Email
                    </Label>
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                      placeholder="usuario o email"
                      data-testid="username-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-white text-sm uppercase tracking-wider mb-2 block">
                      Contraseña
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                      placeholder="••••••••"
                      data-testid="password-input"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 0 40px rgba(251, 176, 64, 0.8)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="group relative overflow-hidden bg-gradient-to-r from-[#fbb040] via-yellow-400 to-[#fbb040] text-black px-10 py-4 rounded-2xl font-black text-lg shadow-2xl flex items-center justify-center gap-3 w-full"
                    data-testid="submit-login-button"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-[#fbb040]"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.6 }}
                    />
                    <span className="relative z-10">
                      {loading ? 'INGRESANDO...' : 'INGRESAR'}
                    </span>
                  </motion.button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="cedula" className="text-white text-sm uppercase tracking-wider mb-2 block">
                      Cédula
                    </Label>
                    <Input
                      id="cedula"
                      type="text"
                      value={cedula}
                      onChange={(e) => setCedula(e.target.value)}
                      required
                      className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                      placeholder="V-12345678"
                      data-testid="cedula-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white text-sm uppercase tracking-wider mb-2 block">
                      Correo Electrónico
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                      placeholder="tu@email.com"
                      data-testid="email-input"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 0 40px rgba(251, 176, 64, 0.8)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="group relative overflow-hidden bg-gradient-to-r from-[#fbb040] via-yellow-400 to-[#fbb040] text-black px-10 py-4 rounded-2xl font-black text-lg shadow-2xl flex items-center justify-center gap-3 w-full"
                    data-testid="submit-validation-button"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-[#fbb040]"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.6 }}
                    />
                    <span className="relative z-10">
                      {loading ? 'VERIFICANDO...' : 'VERIFICAR BENEFICIO'}
                    </span>
                  </motion.button>
                </form>
              )}
            </motion.div>

            {/* Kompaisito Flotante */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="flex justify-center items-center"
            >
              <motion.img
                src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/sqmsw1b6_edited-photo%282%29.png"
                alt="Kompaisito"
                animate={{ 
                  y: [0, -30, 0],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-[280px] h-[280px] lg:w-[346px] lg:h-[346px] z-20 drop-shadow-2xl"
                style={{ transform: 'scaleY(2.778)' }}
              />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] border border-[#F5A623]/30 rounded-2xl p-8 max-w-md" data-testid="not-found-modal">
          <DialogTitle className="text-3xl font-bold text-white mb-4">
            ¡SIGUE ADELANTE!
          </DialogTitle>
          <DialogDescription className="text-lg text-[#666666] leading-relaxed">
            {modalMessage}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
}